import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'

type Blob = { x: number; y: number; r: number; hue: string; sx: number; sy: number; phase: number }
type Mote = { a: number; d: number; s: number; size: number; tw: number }

// Brand palette: cyan, mist, amber, rose, with a deep indigo base.
const BLOBS: Blob[] = [
  { x: 0.18, y: 0.22, r: 0.55, hue: '46,230,197', sx: 0.11, sy: 0.07, phase: 0 },
  { x: 0.82, y: 0.18, r: 0.5, hue: '255,179,71', sx: 0.08, sy: 0.1, phase: 1.7 },
  { x: 0.7, y: 0.85, r: 0.6, hue: '255,107,138', sx: 0.09, sy: 0.06, phase: 3.1 },
  { x: 0.25, y: 0.8, r: 0.5, hue: '96,120,255', sx: 0.07, sy: 0.09, phase: 4.4 },
  { x: 0.5, y: 0.45, r: 0.45, hue: '46,230,197', sx: 0.05, sy: 0.05, phase: 2.2 },
]

/**
 * Full-bleed animated aurora: a drifting gradient mesh drawn on a small canvas (scaled up
 * and blurred by CSS, so it costs almost nothing), plus a field of slow-orbiting motes.
 * Pauses when the tab is hidden; draws one still frame under prefers-reduced-motion.
 */
export function AuroraBackdrop() {
  const meshRef = useRef<HTMLCanvasElement>(null)
  const motesRef = useRef<HTMLCanvasElement>(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    const mesh = meshRef.current
    const motesCanvas = motesRef.current
    if (!mesh || !motesCanvas) return
    const mctx = mesh.getContext('2d')
    const pctx = motesCanvas.getContext('2d')
    if (!mctx || !pctx) return

    const MESH_W = 160
    const MESH_H = 100
    mesh.width = MESH_W
    mesh.height = MESH_H

    const motes: Mote[] = Array.from({ length: 70 }, () => ({
      a: Math.random() * Math.PI * 2,
      d: 0.18 + Math.random() * 0.75,
      s: (Math.random() < 0.5 ? -1 : 1) * (0.008 + Math.random() * 0.025),
      size: 0.4 + Math.random() * 1.4,
      tw: Math.random() * Math.PI * 2,
    }))

    let w = 0
    let h = 0
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const resize = () => {
      w = motesCanvas.clientWidth
      h = motesCanvas.clientHeight
      motesCanvas.width = Math.round(w * dpr)
      motesCanvas.height = Math.round(h * dpr)
      pctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    const drawMesh = (t: number) => {
      mctx.globalCompositeOperation = 'source-over'
      mctx.fillStyle = '#05070f'
      mctx.fillRect(0, 0, MESH_W, MESH_H)
      mctx.globalCompositeOperation = 'lighter'
      for (const b of BLOBS) {
        const cx = (b.x + Math.sin(t * b.sx + b.phase) * 0.18) * MESH_W
        const cy = (b.y + Math.cos(t * b.sy + b.phase) * 0.16) * MESH_H
        const r = b.r * MESH_W * (0.9 + 0.1 * Math.sin(t * 0.3 + b.phase))
        const g = mctx.createRadialGradient(cx, cy, 0, cx, cy, r)
        g.addColorStop(0, `rgba(${b.hue},0.42)`)
        g.addColorStop(0.45, `rgba(${b.hue},0.14)`)
        g.addColorStop(1, `rgba(${b.hue},0)`)
        mctx.fillStyle = g
        mctx.fillRect(0, 0, MESH_W, MESH_H)
      }
    }

    const drawMotes = (t: number) => {
      pctx.clearRect(0, 0, w, h)
      const cx = w / 2
      const cy = h * 0.42
      const span = Math.max(w, h) * 0.62
      for (const m of motes) {
        const a = m.a + t * m.s
        const x = cx + Math.cos(a) * m.d * span
        const y = cy + Math.sin(a) * m.d * span * 0.55
        const alpha = 0.25 + 0.35 * (0.5 + 0.5 * Math.sin(t * 1.3 + m.tw))
        pctx.beginPath()
        pctx.fillStyle = `rgba(232,241,255,${alpha.toFixed(3)})`
        pctx.arc(x, y, m.size, 0, Math.PI * 2)
        pctx.fill()
      }
    }

    if (reduce) {
      drawMesh(0)
      drawMotes(0)
      return () => window.removeEventListener('resize', resize)
    }

    let raf = 0
    let last = 0
    const start = performance.now()
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      if (now - last < 1000 / 40) return // ~40 fps is plenty for slow drift
      last = now
      const t = (now - start) / 1000
      drawMesh(t)
      drawMotes(t)
    }
    const onVisibility = () => {
      cancelAnimationFrame(raf)
      if (!document.hidden) raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [reduce])

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-[var(--color-void)]" aria-hidden>
      <canvas
        ref={meshRef}
        className="absolute inset-[-10%] h-[120%] w-[120%] opacity-90"
        style={{ filter: 'blur(48px) saturate(1.25)' }}
      />
      <div className="afos-grid" />
      <canvas ref={motesRef} className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(5,7,15,0.75)_100%)]" />
    </div>
  )
}
